import { Modal, notification } from "antd";
import React, { FC } from "react";
import Title from "antd/es/typography/Title";
import { useTranslation } from "react-i18next";
import { useFormikContext } from "formik";
import { FormInput, FormSelect } from "shared/ui";
import { Colors } from "shared/themes";
import { handleRequestError } from "shared/helpers";
import { useUpdateOrderPlanMutation } from "shared/api";
import { ModalSizeValue, orderPlanSelectValues } from "shared/constants";
import { AddOrderPlanValues } from "shared/types";

interface EditPlanModalProps {
  isOpenModal: boolean;
  setOpenEditPlanModal: React.Dispatch<React.SetStateAction<boolean>>;
}

const EditPlanModal: FC<EditPlanModalProps> = ({
  isOpenModal,
  setOpenEditPlanModal,
}) => {
  const { t } = useTranslation();
  const [api, contextHolder] = notification.useNotification();
  const {
    values,
    resetForm,
    handleBlur,
    handleChange,
    isValid,
    setFieldValue,
    errors,
  } = useFormikContext<AddOrderPlanValues>();

  const [updateOrderPlan] = useUpdateOrderPlanMutation();

  const updateOrderPlanRequest = async () => {
    try {
      await updateOrderPlan({
        id: values.id!,
        name: values.name!,
        status: values.status!,
      }).unwrap();
      api.success({
        message: t("plans.success-title"),
        description: t("plans.success-update-msg"),
      });
      setOpenEditPlanModal(false);
      resetForm();
    } catch (error) {
      const description = handleRequestError(error, t("plans.error-title"));
      api.error({
        message: t("plans.error-title"),
        description,
      });
    }
  };

  return (
    <>
      {contextHolder}
      <Modal
        title={t("plans.edit-plan")}
        open={isOpenModal}
        width={ModalSizeValue.SECONDARY}
        okButtonProps={{
          disabled: !isValid,
          style: {
            background: `${Colors.blue.primary}`,
          },
        }}
        onCancel={() => {
          setOpenEditPlanModal(false);
          resetForm();
        }}
        onOk={() => {
          updateOrderPlanRequest();
        }}
      >
        <Title level={5} className="mb-1.25">
          {t("plans.name")}
        </Title>

        <FormInput
          value={values.name}
          fieldName="name"
          placeholder={t("plans.name-placeholder")}
          handleChange={handleChange}
          handleBlur={handleBlur}
          error={errors.name}
          isError={!!errors.name}
        />

        <Title level={5}>{t("plans.status")}</Title>

        <FormSelect
          value={values.status}
          placeholder={t("plans.status-placeholder")}
          fieldName="status"
          options={orderPlanSelectValues}
          setFieldValue={setFieldValue}
          handleBlur={handleBlur}
          error={errors.status?.toString()}
          isError={!!errors.status}
        />
      </Modal>
    </>
  );
};

export { EditPlanModal };
